import { z } from 'zod';

// Schema for creating a new coupon
export const createCouponSchema = z
  .object({
    name: z.string().min(1, 'Coupon name is required'),
    discountType: z.enum(['percent', 'amount'], {
      required_error: 'Discount type is required',
    }),
    percent_off: z
      .number()
      .min(0.01, 'Percent off must be at least 0.01')
      .max(100, 'Percent off cannot exceed 100')
      .optional()
      .nullable(),
    amount_off: z
      .number()
      .min(1, 'Amount off must be at least 1 cent')
      .optional()
      .nullable(),
    currency: z.string().optional(),
    duration: z.enum(['once', 'repeating', 'forever'], {
      required_error: 'Duration is required',
    }),
    duration_in_months: z
      .number()
      .min(1, 'Duration must be at least 1 month')
      .optional()
      .nullable(),
    max_redemptions: z
      .number()
      .min(1, 'Max redemptions must be at least 1')
      .optional()
      .nullable(),
    valid_until: z.string().optional().nullable(),
  })
  .refine(
    (data) => {
      // If discount type is percent, percent_off must be provided
      if (data.discountType === 'percent') {
        return data.percent_off !== undefined && data.percent_off !== null;
      }
      return true;
    },
    {
      message: 'Percent off is required when discount type is percentage',
      path: ['percent_off'],
    }
  )
  .refine(
    (data) => {
      // If discount type is amount, amount_off must be provided
      if (data.discountType === 'amount') {
        return data.amount_off !== undefined && data.amount_off !== null;
      }
      return true;
    },
    {
      message: 'Amount off is required when discount type is fixed amount',
      path: ['amount_off'],
    }
  )
  .refine(
    (data) => {
      // If duration is repeating, duration_in_months must be provided
      if (data.duration === 'repeating') {
        return data.duration_in_months !== undefined && data.duration_in_months !== null;
      }
      return true;
    },
    {
      message: 'Duration in months is required when duration is repeating',
      path: ['duration_in_months'],
    }
  );

export type CreateCouponFormData = z.infer<typeof createCouponSchema>;

// Schema for editing an existing coupon
export const editCouponSchema = z.object({
  name: z.string().min(1, 'Coupon name is required'),
  is_active: z.boolean(),
});

export type EditCouponFormData = z.infer<typeof editCouponSchema>;
